import dayjs from 'dayjs'

// Importing the pt-br locale
import 'dayjs/locale/pt-br'

import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import localeData from 'dayjs/plugin/localeData'
// Extending DayJS plugins
import localizedFormat from 'dayjs/plugin/localizedFormat'
import minMax from 'dayjs/plugin/minMax'
import utc from 'dayjs/plugin/utc'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'

// Setting pt-br as the default locale
// NOTE: This is a global setting, consider using the locale parameter in the format method, if you need to format dates in different locales
//
// https://day.js.org/docs/en/i18n/i18n
// https://day.js.org/docs/en/display/format#locale
dayjs.locale('pt-br') // TODO: Review when we support other languages

// Extending DayJS plugins
dayjs.extend(localizedFormat)
dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(minMax)
dayjs.extend(isBetween)
dayjs.extend(weekOfYear)
dayjs.extend(weekday)
dayjs.extend(localeData)

export type DateType = string | number | Date | null | undefined

type DateFormats =
  | 'L' // Format L as defined by Locale. e.g. 'DD/MM/YYYY' for pt-BR or 'MM/DD/YYYY' for en-US
  | 'MM/YY'
  | 'MM/YYYY'
  | 'DD/MM'
  | 'DD/MM/YY'
  | 'DD/MM/YYYY'
  | 'MM'
  | 'MMM' // Jan-Dec
  | 'MMMM' // January-December
  | 'YYYY'
  | 'MMYYYY'
  | 'YYYYMM'
  | 'YYYY-MM'
  | 'YYYY-MM-DD'
  | 'YYYY-MM-DD HH:mm:ss'
  | 'DD/MM/YYYY HH:mm:ss'
  | 'HH:mm'
  | 'HH[h]mm'
  | 'YYYY-MM-DDTHH:mm:ssZ[Z]'
  | 'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
  | 'YYYY-MM-DDTHH:mm:ss'
  | `DD/MM/YYYY [${string}] HH[h]mm`
  | `DD/MM [${string}] HH[h]mm`

type DateUnits =
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'year'
  | 'seconds'
  | 'minutes'
  | 'hours'
  | 'days'
  | 'weeks'
  | 'months'
  | 'years'

/**
 * @param date Date to be formatted
 * @param format {DateFormats} Format to be returned
 * @returns Formatted date
 * @default format 'L' (Output format)
 * @default inputFormat undefined (Input format)
 */
const formatDate = (date: DateType, format: DateFormats = 'L', inputFormat?: DateFormats) => {
  if (!date || date === '') {
    return ''
  }

  if (inputFormat) {
    return dayjs(date, inputFormat).format(format)
  }

  return dayjs(date).format(format)
}

const formatServerDate = (date?: DateType) => {
  if (!date) {
    date = new Date()
  }

  return formatDate(date, 'YYYY-MM-DD HH:mm:ss')
}

const formatSendDate = (date?: DateType) => {
  if (!date) {
    return ''
  }

  return formatDate(date, 'DD/MM/YY')
}

const formatDatePeriod = (
  initialDate: DateType,
  finalDate: DateType,
  separator: string = '-',
  format: DateFormats = 'L',
) => {
  if (!initialDate || !finalDate) {
    return ''
  }

  const initialDateFormatted = formatDate(initialDate, format)
  const finalDateFormatted = formatDate(finalDate, format)

  return `${initialDateFormatted} ${separator} ${finalDateFormatted}`
}

const formatCoteDate = (date: string | Date) => {
  return dayjs(date, 'MM/YYYY').format('MM/YY')
}

/**
 * Compares two dates considering the first day of the month and returns true if they are one year apart.
 * @param initialQuota Date
 * @param finalQuota Date
 * @returns boolean
 */
const oneYearApart = (initialQuota: DateType, finalQuota: DateType) => {
  if (!initialQuota || !finalQuota) {
    return false
  }

  const normalizedInitialQuota = dayjs.utc(initialQuota).startOf('month')
  const normalizedFinalQuota = dayjs.utc(finalQuota).startOf('month')
  const oneYearLater = dayjs.utc(normalizedInitialQuota).add(1, 'year').toDate()

  return dayjs(normalizedFinalQuota).diff(oneYearLater) <= 1
}

const getPreviousYearDate = (currentDate: Date) => {
  return dayjs.utc(currentDate).subtract(1, 'year').toISOString()
}

const getFirstDayOfMonth = (date?: DateType, format: DateFormats = 'L') => {
  return dayjs.utc(date).startOf('month').format(format)
}

const getLastDayOfMonth = (date?: DateType, format: DateFormats = 'L') => {
  return dayjs.utc(date).endOf('month').format(format)
}

const getDiffBetweenDates = (startDate: DateType, endDate: DateType, unit: DateUnits) => {
  return dayjs.utc(endDate).diff(dayjs(startDate), unit)
}

const parseDateToUtc = (date: DateType, inputFormat: DateFormats) => {
  if (inputFormat.includes('Z')) {
    return dayjs(date).utc()
  }

  return dayjs.utc(date, inputFormat)
}

const dateAdd = (
  date: DateType,
  amount: number,
  unit: DateUnits,
  format: DateFormats = 'L',
  inputFormat: DateFormats = 'L',
) => {
  return parseDateToUtc(date, inputFormat).add(amount, unit).format(format)
}

const dateSubtract = (
  date: DateType,
  amount: number,
  unit: DateUnits,
  format: DateFormats = 'L',
  inputFormat: DateFormats = 'L',
) => {
  return parseDateToUtc(date, inputFormat).subtract(amount, unit).format(format)
}

// NOTE: When date is undefined, it is considered as the current date
const isSameDate = (date: DateType, compareDate: DateType) => {
  return dayjs.utc(date).isSame(dayjs.utc(compareDate))
}

// NOTE: When date is undefined, it is considered as the current date
const isDateBefore = (date: DateType, compareDate: DateType) => {
  return dayjs.utc(date).isBefore(dayjs.utc(compareDate))
}

// NOTE: When date is undefined, it is considered as the current date
const isDateAfter = (date: DateType, compareDate: DateType) => {
  return dayjs.utc(date).isAfter(dayjs.utc(compareDate))
}

const isValidDate = (date: DateType, inputFormat: DateFormats) => {
  return dayjs(date, inputFormat, true).isValid()
}

/**
 * Parameter 4 is a string with two characters; '[' means inclusive, '(' exclusive
 * '()' excludes start and end date
 * '[]' includes start and end date (default)
 * '[)' includes the start date but excludes the stop
 * '(]' excludes the start date but includes the stop
 *
 * Granuality offers the precision on start and end inclusive checks.
 * For example including the start date on day precision you should use 'day' as 3rd parameter.
 * https://day.js.org/docs/en/plugin/is-between
 **/
const isDateBetween = (
  date: DateType,
  startDate: DateType,
  endDate: DateType,
  precision: '()' | '[]' | '[)' | '(]' = '[]',
) => {
  return dayjs.utc(date).isBetween(dayjs.utc(startDate), dayjs.utc(endDate), null, precision)
}

const wrapperDateObject = (date: Date | string, format: DateFormats = 'L') => {
  return dayjs(date, format) as unknown as string
}

const getMaxDate = (dates: Date[]) => {
  return dayjs.max(dates.map((date) => dayjs(date)))?.toDate()
}

const getStartOfDay = (date: DateType, inputFormat: DateFormats, outputFormat?: DateFormats) => {
  if (outputFormat) {
    return dayjs(date, inputFormat).startOf('day').format(outputFormat)
  }
  return dayjs(date, inputFormat).startOf('day').toISOString()
}

const getToday = (outputFormat?: DateFormats) => {
  if (outputFormat) {
    return dayjs().startOf('day').format(outputFormat)
  }
  return dayjs().startOf('day').toISOString()
}

const getEndOfDay = (date: DateType, inputFormat: DateFormats, outputFormat?: DateFormats) => {
  if (outputFormat) {
    return dayjs(date, inputFormat).endOf('day').format(outputFormat)
  }
  return dayjs(date, inputFormat).endOf('day').toISOString()
}

export {
  dateAdd,
  dateSubtract,
  formatCoteDate,
  formatDate,
  formatDatePeriod,
  formatSendDate,
  formatServerDate,
  getDiffBetweenDates,
  getEndOfDay,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getMaxDate,
  getPreviousYearDate,
  getStartOfDay,
  getToday,
  isDateAfter,
  isDateBefore,
  isDateBetween,
  isSameDate,
  isValidDate,
  oneYearApart,
  wrapperDateObject,
}
